import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, money } from "@/lib/api";
import { StatusPill, TrustChips, EmptyState, InlineLoader } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  DollarSign, Users, Megaphone, Clock, PlusCircle, Wallet, ArrowRight, Banknote, Share2, Copy, BarChart3, Crown, ShieldAlert,
} from "lucide-react";
import { Brand } from "@/components/Brand";
import PushToggle from "@/components/PushToggle";
import NotificationPreferences from "@/components/NotificationPreferences";

export function StatCard({ icon: Icon, label, value, accent = "blue" }) {
  const bg = accent === "red" ? "bg-ff-red" : accent === "green" ? "bg-ff-green" : "bg-ff-blue";
  return (
    <div className="rounded-xl border border-ff-border bg-white p-5 shadow-sm" data-testid={`stat-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
      <div className="flex items-center justify-between">
        <span className={`flex h-10 w-10 items-center justify-center rounded-lg ${bg}`}>
          <Icon className="h-5 w-5 text-white" />
        </span>
      </div>
      <div className="mt-4 font-heading text-2xl font-extrabold text-ff-blue">{value}</div>
      <div className="text-xs font-semibold uppercase tracking-wider text-ff-medium">{label}</div>
    </div>
  );
}

export default function OrganizerDashboard() {
  const [data, setData] = useState(null);
  const [pro, setPro] = useState(null);

  useEffect(() => {
    api.get("/organizer/dashboard").then((r) => setData(r.data)).catch(() => setData({ error: true }));
    api.get("/pro/me").then((r) => setPro(r.data)).catch(() => setPro(null));
  }, []);

  if (!data) return <InlineLoader label="Loading your dashboard" />;
  const s = data.stats || {};
  const profileIncomplete = !data.organizer_profile?.stripe_onboarding_complete;

  return (
    <div>
      <div className="mb-6 flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
        <div>
          <h1 className="font-heading text-2xl font-extrabold tracking-tight text-ff-blue sm:text-3xl">Organizer Dashboard</h1>
          <p className="mt-1 text-sm text-ff-medium">Fast for donors. Strict for fundraisers. Controlled before payout.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button asChild variant="outline" className="border-ff-border font-semibold text-ff-blue hover:border-ff-red hover:text-ff-red">
            <Link to="/organizer/analytics" data-testid="dashboard-view-analytics"><BarChart3 className="mr-2 h-4 w-4" /> Analytics</Link>
          </Button>
          <Button asChild className="bg-ff-action font-semibold text-white hover:bg-ff-actionDark">
            <Link to="/start-a-fundraiser" data-testid="dashboard-new-campaign"><PlusCircle className="mr-2 h-4 w-4" /> New Fundraiser</Link>
          </Button>
        </div>
      </div>

      {(data.campaigns || []).filter((c) => c.status === "under_review").length > 0 && (
        <div className="mb-6 rounded-xl border border-ff-amber/30 bg-[#FFF7ED] p-5" data-testid="under-review-banner">
          <div className="flex items-start gap-3">
            <ShieldAlert className="mt-0.5 h-6 w-6 shrink-0 text-ff-amber" />
            <div>
              <div className="font-heading font-bold text-ff-blue">
                {(data.campaigns || []).filter((c) => c.status === "under_review").length} campaign{(data.campaigns || []).filter((c) => c.status === "under_review").length > 1 ? "s" : ""} under review
              </div>
              <div className="text-sm text-ff-dark">Our Trust &amp; Safety team is taking a closer look. Open a campaign to see what's needed and respond.</div>
            </div>
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            {(data.campaigns || []).filter((c) => c.status === "under_review").map((c) => (
              <Button key={c.id} asChild size="sm" variant="outline" className="border-ff-amber text-ff-amber hover:bg-ff-amber hover:text-white">
                <Link to={`/organizer/campaign-status/${c.id}`} data-testid={`under-review-link-${c.id}`}>{c.title} <ArrowRight className="ml-1.5 h-3.5 w-3.5" /></Link>
              </Button>
            ))}
          </div>
        </div>
      )}

      {profileIncomplete && (
        <div className="mb-6 flex flex-col items-start justify-between gap-3 rounded-xl border border-ff-amber/30 bg-[#FFF7ED] p-5 sm:flex-row sm:items-center" data-testid="onboarding-banner">
          <div className="flex items-center gap-3">
            <Wallet className="h-6 w-6 text-ff-amber" />
            <div>
              <div className="font-heading font-bold text-ff-blue">Complete your payout onboarding</div>
              <div className="text-sm text-ff-dark">Connect a U.S. payout account through Stripe before you can receive funds.</div>
            </div>
          </div>
          <Button asChild variant="outline" className="border-ff-amber text-ff-amber hover:bg-ff-amber hover:text-white">
            <Link to="/organizer/onboarding" data-testid="onboarding-cta">Finish onboarding</Link>
          </Button>
        </div>
      )}

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard icon={DollarSign} label="Total Raised" value={money(s.total_raised)} accent="green" />
        <StatCard icon={Users} label="Total Donors" value={s.total_donors || 0} />
        <StatCard icon={Megaphone} label="Live Campaigns" value={s.live_campaigns || 0} accent="red" />
        <StatCard icon={Clock} label="Pending Review" value={s.pending_campaigns || 0} />
      </div>

      {pro && !pro.is_pro && (
        <Link to="/nonprofit/dashboard" data-testid="dashboard-pro-upsell"
          className="mt-6 flex flex-col items-start justify-between gap-3 rounded-xl border border-[#FDE68A] bg-gradient-to-r from-[#FFFBEB] to-white p-5 transition-shadow duration-200 hover:shadow-md sm:flex-row sm:items-center">
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#FEF3C7] text-[#92400E]"><Crown className="h-5 w-5" /></span>
            <div>
              <div className="font-heading font-bold text-ff-blue">Upgrade to <Brand /> Pro — from $9.95/mo</div>
              <div className="text-sm text-ff-medium">Branded tax receipts, recurring-donor tools, a Pro badge & priority review.</div>
            </div>
          </div>
          <span className="shrink-0 rounded-lg bg-ff-blue px-4 py-2 text-sm font-semibold text-white">See Pro <ArrowRight className="ml-1 inline h-3.5 w-3.5" /></span>
        </Link>
      )}

      <div className="mt-8 grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="font-heading text-lg font-bold text-ff-blue">Your campaigns</h2>
            <Link to="/organizer/campaigns" className="text-sm font-semibold text-ff-red hover:underline">View all</Link>
          </div>
          {data.campaigns?.length ? (
            <div className="space-y-3">
              {data.campaigns.slice(0, 5).map((c) => (
                <Link key={c.id} to={`/organizer/campaign-status/${c.id}`} data-testid={`dash-campaign-${c.slug}`}
                  className="flex items-center gap-4 rounded-xl border border-ff-border bg-white p-4 shadow-sm transition-shadow duration-200 hover:shadow-md">
                  <div className="h-14 w-20 shrink-0 overflow-hidden rounded-lg bg-[#F3F4F6]">
                    {c.cover_image && <img src={c.cover_image} alt="" className="h-full w-full object-cover" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-heading font-bold text-ff-blue">{c.title}</div>
                    <div className="mt-1 flex items-center gap-2 text-sm text-ff-medium">
                      {money(c.amount_raised)} raised · {c.donor_count || 0} donors
                    </div>
                  </div>
                  <StatusPill status={c.status} />
                </Link>
              ))}
            </div>
          ) : (
            <EmptyState icon={Megaphone} title="No campaigns yet"
              message="Start your first fundraiser and submit it for review."
              action={<Button asChild className="bg-ff-action text-white"><Link to="/start-a-fundraiser">Start a fundraiser</Link></Button>}
              testid="dash-campaigns-empty" />
          )}
        </div>

        <div>
          <PushToggle
            title="Notifications"
            description="Get an instant alert the moment a donation lands, a campaign is approved, or a payout is released — even when the app is closed."
          />
          <NotificationPreferences />
          <h2 className="mb-3 mt-6 font-heading text-lg font-bold text-ff-blue">Payout requests</h2>
          {data.payouts?.length ? (
            <div className="space-y-3">
              {data.payouts.map((p) => (
                <div key={p.id} className="rounded-xl border border-ff-border bg-white p-4 shadow-sm">
                  <div className="flex items-center justify-between">
                    <span className="font-heading font-bold text-ff-blue">{money(p.amount)}</span>
                    <StatusPill status={p.status} />
                  </div>
                  <div className="mt-1 truncate text-xs text-ff-medium">{p.campaign_title}</div>
                </div>
              ))}
              <Button asChild variant="outline" className="w-full border-ff-blue text-ff-blue hover:bg-ff-blue hover:text-white">
                <Link to="/organizer/payout-request" data-testid="dash-request-payout"><Banknote className="mr-2 h-4 w-4" /> Request a payout</Link>
              </Button>
            </div>
          ) : (
            <div className="rounded-xl border border-dashed border-ff-border bg-white p-6 text-center">
              <p className="text-sm text-ff-dark">No payout requests yet.</p>
              <Button asChild variant="link" className="text-ff-red">
                <Link to="/organizer/payout-request">Request a payout <ArrowRight className="ml-1 h-3.5 w-3.5" /></Link>
              </Button>
            </div>
          )}
        </div>
      </div>

      <ShareStats campaigns={data.campaigns || []} totalShares={s.total_shares || 0} />
    </div>
  );
}

function ShareStats({ campaigns, totalShares }) {
  const ranked = [...campaigns]
    .filter((c) => ["live", "approved"].includes(c.status))
    .sort((a, b) => (b.share_count || 0) - (a.share_count || 0));
  const max = Math.max(1, ...ranked.map((c) => c.share_count || 0));

  const copyShare = async (slug, title) => {
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/api/s/${slug}`);
      toast.success(`Share link for "${title}" copied — paste it anywhere!`);
    } catch { toast.error("Could not copy link"); }
  };

  return (
    <div className="mt-8" data-testid="share-stats-widget">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-ff-blue">
          <Share2 className="h-5 w-5 text-ff-action" /> Share performance
        </h2>
        <span className="rounded-full bg-[#ECFDF5] px-3 py-1 text-xs font-bold text-ff-green" data-testid="total-shares">
          {totalShares} total shares
        </span>
      </div>
      {ranked.length === 0 ? (
        <div className="rounded-xl border border-dashed border-ff-border bg-white p-6 text-center text-sm text-ff-dark" data-testid="share-stats-empty">
          Publish a campaign, then share it — sharing is the #1 driver of donations.
        </div>
      ) : (
        <div className="rounded-xl border border-ff-border bg-white p-2 shadow-sm">
          {ranked.map((c, i) => (
            <div key={c.id} className="flex items-center gap-3 rounded-lg px-3 py-3 transition-colors duration-200 hover:bg-[#F9FAFB]" data-testid={`share-row-${c.slug}`}>
              <span className="w-5 shrink-0 text-center font-heading text-sm font-bold text-ff-medium">{i + 1}</span>
              <div className="min-w-0 flex-1">
                <div className="truncate font-heading text-sm font-bold text-ff-blue">{c.title}</div>
                <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-[#F3F4F6]">
                  <div className="h-full rounded-full bg-ff-action" style={{ width: `${Math.round(((c.share_count || 0) / max) * 100)}%` }} />
                </div>
              </div>
              {(c.share_count || 0) === 0 && (
                <span className="hidden rounded-full bg-[#FFF7ED] px-2 py-0.5 text-[10px] font-bold text-ff-amber sm:inline">Give it a push</span>
              )}
              <div className="flex w-14 shrink-0 items-center justify-end gap-1 text-sm font-bold text-ff-blue">
                <Share2 className="h-3.5 w-3.5 text-ff-medium" /> {c.share_count || 0}
              </div>
              <Button size="sm" variant="outline" data-testid={`copy-share-${c.slug}`}
                onClick={() => copyShare(c.slug, c.title)}
                className="shrink-0 border-ff-border text-ff-blue hover:border-ff-red hover:text-ff-red">
                <Copy className="mr-1 h-3.5 w-3.5" /> Copy link
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
